import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PanelLayout from '../layouts/PanelLayout';
import { bugReportService } from '../../services/bugReportService';
import Loader from '../../components/Loader';
import DateTimeDisplay from '../../components/common/DateTimeDisplay';
import Pagination from '../../components/common/Pagination';
import ActionMenu from '../../components/common/ActionMenu';
import ConfirmModal from '../../components/common/ConfirmModal';
import toast from 'react-hot-toast';

const STATUS_BADGE = {
   OPEN: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
   IN_PROGRESS: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
   RESOLVED: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
};

const STATUS_LABEL = { OPEN: 'Open', IN_PROGRESS: 'In Progress', RESOLVED: 'Resolved' };

const BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/api$/, '') ?? '';
const getImageUrl = (relativePath) => `${BASE_URL}/uploads/${relativePath}`;

/* ── Detail Modal ─────────────────────────────────────────── */
const DetailModal = ({ report, onClose }) => {
   if (!report) return null;

   return createPortal(
      <div className="fixed inset-0 z-50 flex items-center justify-center">
         <div className="fixed inset-0 backdrop-blur-[1.5px] bg-[#25223D4D]" onClick={onClose} />
         <div className="relative bg-white dark:bg-dark-secondary rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
               <div className="flex items-center gap-3">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                     Bug Report #{report.id}
                  </h3>
                  <span
                     className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_BADGE[report.status]}`}
                  >
                     {STATUS_LABEL[report.status]}
                  </span>
               </div>
               <button
                  onClick={onClose}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
               >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                     />
                  </svg>
               </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto px-6 py-5 space-y-5">
               {/* Meta row */}
               <div className="flex flex-wrap gap-4 text-sm">
                  <div>
                     <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">
                        Tool
                     </p>
                     <span className="font-mono font-medium text-blue-600 dark:text-dark-accent bg-blue-50 dark:bg-dark-accent/10 px-2 py-0.5 rounded text-xs">
                        {report.tool}
                     </span>
                  </div>
                  {report.email && (
                     <div>
                        <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">
                           Email
                        </p>
                        <p className="text-gray-700 dark:text-gray-300">{report.email}</p>
                     </div>
                  )}
                  <div>
                     <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">
                        Submitted
                     </p>
                     <p className="text-gray-700 dark:text-gray-300">
                        <DateTimeDisplay dateStr={report.createdAt} />
                     </p>
                  </div>
                  {report.ipAddress && (
                     <div>
                        <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">
                           IP
                        </p>
                        <p className="text-gray-500 dark:text-gray-400 font-mono text-xs">
                           {report.ipAddress}
                        </p>
                     </div>
                  )}
               </div>

               {/* Description */}
               <div>
                  <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-2">
                     Description
                  </p>
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                     {report.description}
                  </p>
               </div>

               {/* Screenshots */}
               {report.images?.length > 0 && (
                  <div>
                     <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-3">
                        Screenshots ({report.images.length})
                     </p>
                     <div className="flex flex-wrap gap-3">
                        {report.images.map((img, i) => (
                           <a
                              key={i}
                              href={getImageUrl(img)}
                              target="_blank"
                              rel="noopener noreferrer"
                           >
                              <img
                                 src={getImageUrl(img)}
                                 alt={`screenshot ${i + 1}`}
                                 className="w-36 h-28 object-cover rounded-lg border border-gray-200 dark:border-gray-700 hover:opacity-80 transition-opacity"
                              />
                           </a>
                        ))}
                     </div>
                  </div>
               )}
            </div>
         </div>
      </div>,
      document.body
   );
};

/* ── Page ─────────────────────────────────────────────────── */
const BugReports = () => {
   const [reports, setReports] = useState([]);
   const [loading, setLoading] = useState(true);
   const [page, setPage] = useState(1);
   const [totalPages, setTotalPages] = useState(1);
   const [total, setTotal] = useState(0);
   const [viewTarget, setViewTarget] = useState(null);
   const [deleteTarget, setDeleteTarget] = useState(null);
   const [deletingId, setDeletingId] = useState(null);
   const [updatingId, setUpdatingId] = useState(null);
   const LIMIT = 15;

   const fetchReports = useCallback(async () => {
      try {
         setLoading(true);
         const res = await bugReportService.getAll({ page, limit: LIMIT });
         const { reports: data, totalPages: tp, total: t } = res.data.data;
         setReports(data);
         setTotalPages(tp);
         setTotal(t);
      } catch {
         toast.error('Failed to load bug reports');
      } finally {
         setLoading(false);
      }
   }, [page]);

   useEffect(() => {
      fetchReports();
   }, [fetchReports]);

   const handleStatusChange = async (id, status) => {
      try {
         setUpdatingId(id);
         await bugReportService.updateStatus(id, status);
         setReports((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
         // Keep modal in sync if it's open for this report
         setViewTarget((prev) => (prev?.id === id ? { ...prev, status } : prev));
         toast.success('Status updated');
      } catch {
         toast.error('Failed to update status');
      } finally {
         setUpdatingId(null);
      }
   };

   const handleDelete = async () => {
      if (!deleteTarget) return;
      try {
         setDeletingId(deleteTarget.id);
         await bugReportService.delete(deleteTarget.id);
         toast.success('Bug report deleted');
         setDeleteTarget(null);
         setViewTarget(null);
         fetchReports();
      } catch {
         toast.error('Failed to delete bug report');
      } finally {
         setDeletingId(null);
      }
   };

   const buildMenuItems = (report) => {
      const statusItems = [
         { id: 'OPEN', label: 'Mark as Open' },
         { id: 'IN_PROGRESS', label: 'Mark as In Progress' },
         { id: 'RESOLVED', label: 'Mark as Resolved' },
      ]
         .filter((s) => s.id !== report.status)
         .map((s) => ({
            label: s.label,
            disabled: updatingId === report.id,
            onClick: () => handleStatusChange(report.id, s.id),
         }));

      return [
         {
            label: 'View Details',
            onClick: () => setViewTarget(report),
         },
         ...statusItems,
         {
            label: 'Delete',
            variant: 'danger',
            onClick: () => setDeleteTarget(report),
         },
      ];
   };

   if (loading && reports.length === 0) {
      return (
         <PanelLayout>
            <Loader />
         </PanelLayout>
      );
   }

   return (
      <PanelLayout>
         <div className="space-y-6">
            {/* Header */}
            <div>
               <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Bug Reports</h1>
               <p className="mt-1 text-gray-600 dark:text-gray-400">
                  {total} report{total !== 1 ? 's' : ''} total
               </p>
            </div>

            {/* Table */}
            <div className="rounded-lg bg-white dark:bg-dark-secondary shadow-md overflow-hidden">
               <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                     <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                           <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              #
                           </th>
                           <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Tool
                           </th>
                           <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Description
                           </th>
                           <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Images
                           </th>
                           <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Email
                           </th>
                           <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Status
                           </th>
                           <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Date
                           </th>
                           <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Actions
                           </th>
                        </tr>
                     </thead>
                     <tbody className="bg-white dark:bg-dark-secondary divide-y divide-gray-200 dark:divide-gray-700">
                        {loading ? (
                           <tr>
                              <td
                                 colSpan="8"
                                 className="px-4 py-8 text-center text-gray-500 dark:text-gray-400"
                              >
                                 Loading…
                              </td>
                           </tr>
                        ) : reports.length === 0 ? (
                           <tr>
                              <td
                                 colSpan="8"
                                 className="px-4 py-8 text-center text-gray-500 dark:text-gray-400"
                              >
                                 No bug reports yet
                              </td>
                           </tr>
                        ) : (
                           reports.map((report) => (
                              <tr
                                 key={report.id}
                                 className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                                 onClick={() => setViewTarget(report)}
                              >
                                 <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">
                                    {report.id}
                                 </td>
                                 <td className="px-4 py-4 whitespace-nowrap">
                                    <span className="text-xs font-mono font-medium text-blue-600 dark:text-dark-accent bg-blue-50 dark:bg-dark-accent/10 px-2 py-0.5 rounded">
                                       {report.tool}
                                    </span>
                                 </td>
                                 <td className="px-4 py-4 max-w-xs">
                                    <p className="text-sm text-gray-700 dark:text-gray-300 truncate">
                                       {report.description}
                                    </p>
                                 </td>
                                 <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    {report.images?.length > 0 ? (
                                       <span className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                                          {report.images.length} image
                                          {report.images.length !== 1 ? 's' : ''}
                                       </span>
                                    ) : (
                                       <span className="text-gray-300 dark:text-gray-600">N/A</span>
                                    )}
                                 </td>
                                 <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                                    {report.email || (
                                       <span className="text-gray-300 dark:text-gray-600">N/A</span>
                                    )}
                                 </td>
                                 <td className="px-4 py-4 whitespace-nowrap">
                                    <span
                                       className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_BADGE[report.status]}`}
                                    >
                                       {STATUS_LABEL[report.status]}
                                    </span>
                                 </td>
                                 <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    <DateTimeDisplay dateStr={report.createdAt} />
                                 </td>
                                 <td
                                    className="px-4 py-4 whitespace-nowrap"
                                    onClick={(e) => e.stopPropagation()}
                                 >
                                    <ActionMenu items={buildMenuItems(report)} />
                                 </td>
                              </tr>
                           ))
                        )}
                     </tbody>
                  </table>
               </div>
            </div>

            <Pagination
               page={page}
               totalPages={totalPages}
               total={total}
               limit={LIMIT}
               onPageChange={setPage}
            />
         </div>

         <DetailModal report={viewTarget} onClose={() => setViewTarget(null)} />

         <ConfirmModal
            isOpen={!!deleteTarget}
            title="Delete Bug Report"
            message={`Delete bug report #${deleteTarget?.id}? This will also remove any uploaded screenshots. This cannot be undone.`}
            confirmLabel="Delete"
            confirmVariant="danger"
            onConfirm={handleDelete}
            onCancel={() => setDeleteTarget(null)}
            loading={!!deletingId}
         />
      </PanelLayout>
   );
};

export default BugReports;
